import { PLATFORM_INSTITUTION_CODE } from "@eduleo/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Menu, School, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { logout, useMe } from "../../features/auth/auth-api";
import { NotificationBell } from "../../features/notifications/notification-bell";
import { cn } from "../../lib/cn";
import { signInPathFor } from "../../lib/sign-in-paths";
import { useAuthStore } from "../../stores/auth-store";
import { useUiStore } from "../../stores/ui-store";
import { Skeleton } from "../ui/feedback";
import { Logo } from "../ui/logo";
import { type NavItem, navItemsFor } from "./nav-items";
import { useOwnSchoolLogoUrl } from "./own-school-logo";
import { UserMenu } from "./user-menu";

function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <ul className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon, end }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex h-11 items-center gap-3 rounded-md px-3 font-medium text-ink-2 md:h-[38px]",
                "hover:bg-surface-2 hover:text-ink",
                isActive &&
                  "bg-brand-soft font-bold text-brand-fg hover:bg-brand-soft hover:text-brand-fg",
              )
            }
          >
            <Icon aria-hidden className="size-[18px]" strokeWidth={1.5} />
            {label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function SchoolCard() {
  const institution = useAuthStore((state) => state.user?.institution);
  const platform = institution?.code === PLATFORM_INSTITUTION_CODE;
  const logo = useOwnSchoolLogoUrl(platform ? null : institution?.logoUpdatedAt);
  if (!institution) return null;
  return (
    <div className="gradient-brand flex items-center gap-3 rounded-lg px-3 py-3 text-white">
      {logo ? (
        <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-md bg-white">
          <img src={logo} alt="" className="size-full object-contain p-0.5" />
        </span>
      ) : (
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-white/20">
          <School aria-hidden className="size-[18px]" strokeWidth={1.5} />
        </span>
      )}
      <span className="min-w-0">
        <span className="block truncate font-semibold text-sm">
          {platform ? "Platform console" : institution.name}
        </span>
        <span className="block truncate font-mono text-white/80 text-xs">{institution.code}</span>
      </span>
    </div>
  );
}

/** Phone navigation drawer, built on <dialog> so focus stays inside while it's open. */
function NavDrawer({ items }: { items: NavItem[] }) {
  const open = useUiStore((state) => state.navOpen);
  const setOpen = useUiStore((state) => state.setNavOpen);
  const ref = useRef<HTMLDialogElement>(null);
  const location = useLocation();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close?.();
  }, [open]);

  // Close the drawer whenever the page changes.
  useEffect(() => {
    void location.pathname;
    setOpen(false);
  }, [location.pathname, setOpen]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the click only catches taps on the backdrop; keyboard users close with Escape (onCancel) or the Close button.
    <dialog
      ref={ref}
      aria-label="Main menu"
      onCancel={(event) => {
        event.preventDefault();
        setOpen(false);
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) setOpen(false);
      }}
      className="m-0 h-dvh max-h-dvh w-[86vw] max-w-[320px] bg-surface p-0 backdrop:bg-scrim md:hidden"
    >
      {open && (
        <div className="flex h-full flex-col gap-5 p-4">
          <div className="flex items-center justify-between">
            <Logo />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="grid size-11 cursor-pointer place-items-center rounded-md text-ink-2 hover:bg-surface-2"
            >
              <X className="size-5" strokeWidth={1.5} />
            </button>
          </div>
          <SchoolCard />
          <nav aria-label="Main">
            <NavList items={items} onNavigate={() => setOpen(false)} />
          </nav>
        </div>
      )}
    </dialog>
  );
}

export function AppShell() {
  const me = useMe();
  const items = navItemsFor(me.data?.capabilities);
  const setNavOpen = useUiStore((state) => state.setNavOpen);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  // Only Eduleo's own team has notifications for now.
  const platformTeam = useAuthStore(
    (state) => state.user?.institution.code === PLATFORM_INSTITUTION_CODE,
  );

  async function signOut() {
    setSigningOut(true);
    // Eduleo's team goes back to its own sign-in page, schools to theirs.
    const signInPage = signInPathFor(useAuthStore.getState().user);
    try {
      await logout();
    } catch {
      // The local session is cleared either way; the server session expires on its own.
    } finally {
      queryClient.clear();
      navigate(signInPage, { replace: true });
    }
  }

  return (
    <div className="min-h-dvh bg-canvas md:pl-[248px] print:bg-white print:pl-0">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-surface px-4 py-2 font-semibold focus:not-sr-only focus:fixed focus:top-3 focus:left-3 print:hidden"
      >
        Skip to content
      </a>

      <aside className="fixed inset-y-0 left-0 hidden w-[248px] flex-col gap-6 border-edge border-r bg-surface px-4 py-5 md:flex print:hidden">
        <Logo showTagline />
        <SchoolCard />
        <nav aria-label="Main">
          <NavList items={items} />
          {/* The rest of the menu depends on the person's permissions: placeholders until known. */}
          {me.isPending && (
            <div className="mt-1 flex flex-col gap-2 px-3" aria-hidden>
              {[0, 1, 2].map((item) => (
                <Skeleton key={item} className="h-6 w-full" />
              ))}
            </div>
          )}
        </nav>
      </aside>

      <NavDrawer items={items} />

      <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-edge border-b bg-surface/95 px-4 backdrop-blur md:px-6 print:hidden">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
          className="grid size-11 cursor-pointer place-items-center rounded-md text-ink-2 hover:bg-surface-2 md:hidden"
        >
          <Menu className="size-5" strokeWidth={1.5} />
        </button>
        <span className="md:hidden">
          <Logo />
        </span>
        <div className="ml-auto flex items-center gap-1">
          {platformTeam && <NotificationBell />}
          <UserMenu onSignOut={signOut} signingOut={signingOut} />
        </div>
      </header>

      <main
        id="main"
        className="mx-auto w-full max-w-[1440px] px-4 pt-5 pb-28 md:px-6 md:pt-6 md:pb-10"
      >
        <Outlet />
      </main>

      <nav
        aria-label="Quick links"
        className="fixed inset-x-0 bottom-0 z-30 border-edge border-t bg-surface pb-safe md:hidden print:hidden"
      >
        <ul className="flex">
          {items
            .filter((item) => !item.menuOnly)
            .map(({ to, label, icon: Icon, end }) => (
              <li key={to} className="flex-1">
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      "flex h-16 flex-col items-center justify-center gap-1 font-semibold text-2xs text-ink-3",
                      isActive && "text-brand-fg",
                    )
                  }
                >
                  <Icon aria-hidden className="size-5" strokeWidth={1.5} />
                  {label}
                </NavLink>
              </li>
            ))}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              className="flex h-16 w-full cursor-pointer flex-col items-center justify-center gap-1 font-semibold text-2xs text-ink-3"
            >
              <Menu aria-hidden className="size-5" strokeWidth={1.5} />
              More
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}
